/**
 * useSellerDirectory — TanStack Query lookup of the public seller finder, keyed
 * by `seller_id`, for the inline chat quote `seller_match` cards (design
 * revamp).
 *
 * The streamed `seller_match` card carries only id + name + price + unit +
 * cheapest; the design `SellerMiniCard` also shows a rating + district line.
 * Those live on the public seller finder (`GET /marketplace/sellers`,
 * `@/lib/garden-api` `listSellers`), so we fetch the directory once (long
 * staleTime — it is public, slow-changing catalogue data, NO PII) and expose an
 * O(1) `Map` the card reads by id. A miss (seller not in the directory, or the
 * query still loading / errored) degrades the card gracefully to name + price
 * only — never a fabricated rating.
 *
 * Mirrors the marketplace data-hook shape (`useMarketplaceBrowse.ts`): a thin
 * `apiClient` fetcher wrapped in a query, hand-written types from
 * `@/lib/garden-api` (no codegen yet).
 */
import { useQuery } from "@tanstack/react-query";

import { listSellers, type SellerSummary } from "@/lib/garden-api";

/** Stable query key for the (unfiltered) seller directory. */
export function sellerDirectoryQueryKey(): readonly unknown[] {
  return ["chat", "seller-directory"];
}

/** Public catalogue data — cache for 5 minutes (slow-changing, no PII). */
export const SELLER_DIRECTORY_STALE_TIME_MS = 5 * 60_000;

export interface SellerDirectory {
  /** Seller summaries keyed by `seller_id` (empty while loading / on error). */
  byId: ReadonlyMap<string, SellerSummary>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetch + cache the full seller list and index it by id. The result is a stable
 * `Map` recomputed only when the underlying data changes (TanStack Query keeps
 * `data` referentially stable between renders).
 */
export function useSellerDirectory(): SellerDirectory {
  const query = useQuery({
    queryKey: sellerDirectoryQueryKey(),
    queryFn: () => listSellers(),
    staleTime: SELLER_DIRECTORY_STALE_TIME_MS,
  });

  const byId = new Map<string, SellerSummary>();
  for (const seller of query.data?.sellers ?? []) {
    byId.set(seller.id, seller);
  }

  return {
    byId,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
